import { useAuth } from '@commander/auth';
import {
  SafeHtml,
  useEmailSignature,
  useEmailSignatures,
  useUploadSignatureImage,
} from '@commander/communications';
import { Label, toast } from '@commander/ui';
import { Editor } from '@commander/ui/components/blocks/editor/editor';
import { PUSHER_CHANNELS, PUSHER_EVENTS, usePusher } from '@commander/utils';
import { useEffect, useRef, useState } from 'react';
import type { CommunicationStyle, StepProps } from '../../types/onboarding.types';
import { validateEmailSettings } from '../../utils/validators';
import CommunicationStyleSelector from '../shared/CommunicationStyleSelector';

const EmailSettingsStep: React.FC<StepProps> = ({
  data,
  onChange,
  validation,
  onNext,
  isLoading,
  error,
}) => {
  // Use Lexical JSON for signature (rich editor) instead of plain text
  const [signatureBody, setSignatureBody] = useState<any>(null);
  const [communicationStyle, setCommunicationStyle] = useState<CommunicationStyle>(
    data.emailSettings.communicationStyle || 'DEFAULT'
  );
  const [customStyleText, setCustomStyleText] = useState(
    data.emailSettings.communicationStyleText || ''
  );
  const [localError, setLocalError] = useState<string | null>(null);
  // Shows the Lexical editor in place of an imported-signature preview once
  // the user chooses to write their own instead of keeping the imported one.
  const [writingOwnSignature, setWritingOwnSignature] = useState(false);
  // The Editor only applies `editorSerializedState` once, at mount — it won't
  // pick up a body set after the fact. Both signature sources below (a value
  // already on `data`, e.g. returning to this step, and the async-fetched
  // default signature) can arrive after the Editor has already mounted blank,
  // so this key changes at the exact moment either source is first applied,
  // forcing a remount with the loaded content.
  const [editorKey, setEditorKey] = useState('onboarding-signature-editor');
  // Guards that "first applied" moment to exactly once per mount — without
  // it, the user's own typing (which round-trips through onChange back into
  // `data.emailSettings.signature`) would re-trigger the effect below on
  // every keystroke and remount the editor mid-edit, wiping focus/cursor.
  const hasInitializedEditorRef = useRef(false);

  // Image upload mutation for signature images
  const uploadImageMutation = useUploadSignatureImage();
  const { user } = useAuth();
  const { subscribe, unsubscribe } = usePusher();

  // A signature may already exist by the time this step loads — e.g. imported
  // automatically from the user's Gmail account at sign-in. That import now
  // runs in a background worker (see scheduleImportGmailSignature /
  // importGmailSignatureWorker) rather than inline during the OAuth callback,
  // so it can still be in flight when onboarding reaches this step. The worker
  // fires PUSHER_EVENTS.GMAIL_SIGNATURE_IMPORT_SETTLED on the user's channel
  // once it's done (success or failure, one event for both — see the worker
  // comment); a bounded fallback timer covers the race where the import
  // already settled before this step mounted and subscribed, or the event is
  // otherwise missed.
  const IMPORT_WAIT_TIMEOUT_MS = 30000;
  const { data: signaturesList, refetch: refetchSignatures } = useEmailSignatures({ limit: 50 });
  const defaultSignatureId = signaturesList?.signatures.find((s: any) => s.isDefault)?.id;
  const { data: defaultSignature } = useEmailSignature(defaultSignatureId || '', {
    enabled: Boolean(defaultSignatureId),
  });

  useEffect(() => {
    if (!user?.id || defaultSignatureId || hasInitializedEditorRef.current) return;

    const channelName = PUSHER_CHANNELS.USER(user.id);
    const channel = subscribe(channelName);

    const handleImportSettled = () => {
      refetchSignatures();
    };

    channel.bind(PUSHER_EVENTS.GMAIL_SIGNATURE_IMPORT_SETTLED, handleImportSettled);
    const fallbackTimer = setTimeout(refetchSignatures, IMPORT_WAIT_TIMEOUT_MS);

    return () => {
      channel.unbind(PUSHER_EVENTS.GMAIL_SIGNATURE_IMPORT_SETTLED, handleImportSettled);
      unsubscribe(channelName);
      clearTimeout(fallbackTimer);
    };
  }, [user?.id, defaultSignatureId, refetchSignatures, subscribe, unsubscribe]);
  const importedHtmlSignature =
    defaultSignature?.contentType === 'html' ? defaultSignature.htmlBody : undefined;
  // Once the user has authored their own signature (typed fresh, or loaded
  // from `data.emailSettings.signature` on returning to this step), keep the
  // editor visible instead of reverting to the Gmail-import preview.
  const showImportedPreview = importedHtmlSignature && !writingOwnSignature && !signatureBody;

  // Load a signature already present on `data` (e.g. returning to this step
  // after it was filled in earlier) before the user types anything new. Only
  // the first successful application remounts the Editor (via editorKey);
  // later invocations — the user's own typing echoed back through onChange —
  // are guarded out by hasInitializedEditorRef so they don't reset it.
  useEffect(() => {
    if (hasInitializedEditorRef.current || !data.emailSettings.signature) return;
    try {
      const parsed = JSON.parse(data.emailSettings.signature);
      if (parsed?.root?.type) {
        hasInitializedEditorRef.current = true;
        setSignatureBody(parsed);
        setEditorKey('onboarding-signature-editor-initial');
      }
    } catch {
      // Not valid JSON — leave for the defaultSignature effect or the user to fill in.
    }
  }, [data.emailSettings.signature]);

  // Pre-fill from an existing default signature the first time this step
  // loads with nothing typed yet. HTML-type signatures (e.g. Gmail imports)
  // aren't representable in this Lexical editor — those are shown as a
  // read-only preview below instead, and intentionally left out of
  // `data.emailSettings.signature` so onboarding's completion step (which
  // only creates a new signature when that field is set) doesn't create a
  // redundant duplicate of the one already saved.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when the fetched default signature changes; updateEmailSettings is recreated each render and including it would refire this on every keystroke
  useEffect(() => {
    if (hasInitializedEditorRef.current || !defaultSignature) return;
    if (defaultSignature.contentType === 'lexical' && defaultSignature.lexicalBody) {
      hasInitializedEditorRef.current = true;
      setSignatureBody(defaultSignature.lexicalBody);
      updateEmailSettings({ signature: JSON.stringify(defaultSignature.lexicalBody) });
      setEditorKey(`onboarding-signature-editor-${defaultSignature.id}`);
    }
  }, [defaultSignature]);

  const handleStyleChange = (style: CommunicationStyle) => {
    setCommunicationStyle(style);
    updateEmailSettings({ communicationStyle: style });
    setLocalError(null);
  };

  const handleCustomStyleTextChange = (value: string) => {
    setCustomStyleText(value);
    updateEmailSettings({ communicationStyleText: value });
    setLocalError(null);
  };

  // Image upload handler - uploads to GCS and returns URL
  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      const gcsUrl = await uploadImageMutation.mutateAsync(file);
      return gcsUrl;
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload image');
      throw error;
    }
  };

  const updateEmailSettings = (updates: Partial<typeof data.emailSettings>) => {
    const newEmailSettings = {
      ...data.emailSettings,
      ...updates,
    };
    onChange({ ...data, emailSettings: newEmailSettings });
  };

  const handleSubmit = async () => {
    const emailSettings = {
      signature: signatureBody ? JSON.stringify(signatureBody) : '',
      communicationStyle,
      communicationStyleText: customStyleText,
    };

    const localValidation = validateEmailSettings(emailSettings);
    if (!localValidation.isValid) {
      setLocalError(localValidation.error || 'Please check your input');
      return;
    }

    // Update parent state
    updateEmailSettings(emailSettings);
    setLocalError(null);

    // Validate and proceed using the validation prop
    const isValid = await validation({ ...data, emailSettings });
    if (isValid) {
      onNext();
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Email Signature */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="signature-editor">Email Signature</Label>
          <p className="text-xs text-gray-600 mb-2">
            {showImportedPreview
              ? 'We imported this from your connected email account.'
              : 'Create your email signature.'}
          </p>
        </div>
        {showImportedPreview ? (
          <div className="space-y-2 max-w-[600px]">
            <div className="border rounded-md p-4 bg-muted/30">
              <SafeHtml html={importedHtmlSignature} />
            </div>
            <button
              type="button"
              className="text-xs text-primary underline"
              onClick={() => setWritingOwnSignature(true)}
            >
              Write my own signature instead
            </button>
          </div>
        ) : (
          <div className="min-h-[200px] max-w-[600px] border rounded-md overflow-x-auto">
            <Editor
              key={editorKey}
              editorSerializedState={signatureBody || undefined}
              onSerializedChange={(newContent) => {
                setSignatureBody(newContent);
                updateEmailSettings({
                  signature: newContent ? JSON.stringify(newContent) : '',
                });
              }}
              onImageUpload={handleImageUpload}
              placeholder="Enter your email signature (e.g., Best regards, John Doe | Sales Manager | Company Name)"
            />
          </div>
        )}
      </div>

      {/* Communication Style */}
      <CommunicationStyleSelector
        selectedStyle={communicationStyle}
        customStyleText={customStyleText}
        onStyleChange={handleStyleChange}
        onCustomStyleTextChange={handleCustomStyleTextChange}
        className="w-full"
      />

      {/* Error Display */}
      {(error || localError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || localError}
        </div>
      )}

      {/* Hidden navigation for parent component to handle */}
      <div className="hidden">
        <button type="button" onClick={handleSubmit} disabled={isLoading} />
      </div>
    </div>
  );
};

export default EmailSettingsStep;
